import React, { useState } from 'react';
import { Input, Form, InputNumber, Modal, Button, Select, Row, Col, Spin, notification } from 'antd';
import { handleFormChange, validateMessages } from '../../../shared/Utils';
import { useCreateCityMutation } from '../../../GraphQL/hooks/useCreateCityMutation';

// Gouvernorats par pays
const governoratesByCountry: Record<string, string[]> = {
    Tunisie: [
        'Tunis',
        'Ariana',
        'Ben Arous',
        'Manouba',
        'Nabeul',
        'Zaghouan',
        'Bizerte',
        'Béja',
        'Jendouba',
        'Kef',
        'Siliana',
        'Kairouan',
        'Kasserine',
        'Sidi Bouzid',
        'Sousse',
        'Monastir',
        'Mahdia',
        'Sfax',
        'Gabès',
        'Médenine',
        'Tataouine',
        'Gafsa',
        'Tozeur',
        'Kebili',
    ],
    Algerie: ['Alger', 'Annaba', 'Constantine', 'Oran', 'Tizi Ouzou'],
    Marocco: ['Casablanca', 'Rabat', 'Marrakech', 'Fes', 'Tangier'],
};

const CityCreateModal = ({ isOpen, onRequestClose, updateList }) => {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const { mutation, loading: creating } = useCreateCityMutation();

    const handleCommit = async () => {
        try {
            const values = await form.validateFields();
            if (values) {
                await mutation({ ...values })
                    .then(() => {
                        onRequestClose(false);
                        updateList();
                        form.resetFields();
                    })
                    .finally(() => {
                        notification.success({
                            message: 'City created successfully',
                            description: 'The city has been created successfully.',
                        });
                    })
                    .catch((err) => {
                        notification.error({
                            message: 'Failed to create city',
                            description: 'An error occurred while creating the city.',
                        });

                        console.error('Failed to update pricing:', err);
                    });
            } else {
                notification.error({
                    message: 'Form validation failed',
                    description: 'Please check the form for errors and try again.',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Form validation failed',
                description: 'Please check the form for errors and try again.',
            });
            console.error('Form validation failed:', error);
        }
    };

    const handleCancel = () => {
        onRequestClose(false);
    };

    const handleCountryChange = (value: string) => {
        setSelectedCountry(value);
        form.setFieldsValue({ governorate: undefined });
    };

    return (
        <Modal
            title={<h3 style={{ fontWeight: 600, marginBottom: 0 }}>Add City</h3>}
            open={isOpen}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="add" type="primary" onClick={handleCommit} disabled={formError}>
                    Add
                </Button>,
            ]}
            centered
        >
            <Spin spinning={creating}>
                <Form
                    form={form}
                    layout="vertical"
                    onFieldsChange={() => setFormError(handleFormChange(form))}
                    validateMessages={validateMessages}
                >
                    <Form.Item
                        name="country"
                        label="Country"
                        rules={[{ required: true, message: 'Please select a country' }]}
                    >
                        <Select placeholder="Select country" size="large" onChange={handleCountryChange}>
                            <Select.Option value="Tunisie">Tunisie</Select.Option>
                            <Select.Option value="Algerie">Algerie</Select.Option>
                            <Select.Option value="Marocco">Marocco</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="governorate"
                        label="Governorate"
                        rules={[{ required: true, message: 'Please select a governorate' }]}
                    >
                        <Select
                            placeholder={selectedCountry ? 'Select governorate' : 'Select a country first'}
                            size="large"
                            disabled={!selectedCountry}
                        >
                            {selectedCountry &&
                                governoratesByCountry[selectedCountry]?.map((gov) => (
                                    <Select.Option key={gov} value={gov}>
                                        {gov}
                                    </Select.Option>
                                ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="cityName"
                        label="City name"
                        rules={[{ required: true, message: 'Please enter the city name' }]}
                    >
                        <Input placeholder="City" size="large" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="latitude"
                                label="Latitude"
                                rules={[{ required: true, message: 'Please enter latitude' }]}
                            >
                                <InputNumber placeholder="Latitude" style={{ width: '100%' }} size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="longitude"
                                label="Longitude"
                                rules={[{ required: true, message: 'Please enter longitude' }]}
                            >
                                <InputNumber placeholder="Longitude" style={{ width: '100%' }} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
};

export default CityCreateModal;
